import { useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth';
import { SkeletonTable } from '../../components/Skeleton';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.profitos.pro';

interface QueueItem {
  id: string;
  contentId: string;
  contentTitle: string;
  channelId: string;
  channelName: string;
  status: 'pending' | 'scheduled' | 'publishing' | 'published' | 'failed';
  scheduledAt: string | null;
  publishedAt: string | null;
  error: string | null;
  createdAt: string;
}

interface QueueResponse {
  success: boolean;
  data?: {
    queue: QueueItem[];
  };
}

const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  pending: { bg: 'bg-gray-100', text: 'text-gray-700' },
  scheduled: { bg: 'bg-blue-100', text: 'text-blue-700' },
  publishing: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  published: { bg: 'bg-green-100', text: 'text-green-700' },
  failed: { bg: 'bg-red-100', text: 'text-red-700' },
};

export function QueueTab(): ReactNode {
  const { accessToken } = useAuthStore();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<'all' | 'pending' | 'published' | 'failed'>('all');

  const { data, isLoading, error } = useQuery({
    queryKey: ['publishing-queue', filter],
    queryFn: async (): Promise<QueueResponse> => {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await fetch(`${API_URL}/publishing/queue${params}`, {
        headers: {
          Authorization: `Bearer ${accessToken ?? ''}`,
        },
      });

      if (!res.ok) {
        if (res.status === 404) {
          return { success: true, data: { queue: [] } };
        }
        throw new Error('Failed to fetch publishing queue');
      }

      return res.json();
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (itemId: string) => {
      const res = await fetch(`${API_URL}/publishing/queue/${itemId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken ?? ''}`,
        },
      });

      if (!res.ok) {
        throw new Error('Failed to cancel item');
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['publishing-queue'] });
    },
  });

  const retryMutation = useMutation({
    mutationFn: async (itemId: string) => {
      const res = await fetch(`${API_URL}/publishing/queue/${itemId}/retry`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken ?? ''}`,
        },
      });

      if (!res.ok) {
        throw new Error('Failed to retry item');
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['publishing-queue'] });
    },
  });

  const queue = data?.data?.queue ?? [];

  if (isLoading) {
    return <SkeletonTable rows={5} columns={5} />;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-red-600">Failed to load publishing queue. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Publishing Queue</h3>
          <p className="text-sm text-gray-500">Track and manage scheduled content publications</p>
        </div>
        <div className="flex items-center gap-2">
          {(['all', 'pending', 'published', 'failed'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                filter === status
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {queue.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No items in queue</h3>
          <p className="mt-2 text-gray-500">
            {filter === 'all'
              ? 'Schedule content from the Content page to see it here.'
              : `No ${filter} items found.`}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Content
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Channel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Scheduled
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {queue.map((item) => {
                const statusStyle = STATUS_STYLES[item.status] ?? STATUS_STYLES['pending'];
                return (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-900">{item.contentTitle}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.channelName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${statusStyle?.bg ?? 'bg-gray-100'} ${statusStyle?.text ?? 'text-gray-700'}`}>
                        {item.status}
                      </span>
                      {item.error && (
                        <p className="mt-1 text-xs text-red-500 max-w-xs truncate" title={item.error}>
                          {item.error}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.scheduledAt
                        ? new Date(item.scheduledAt).toLocaleString()
                        : item.publishedAt
                        ? `Published ${new Date(item.publishedAt).toLocaleString()}`
                        : 'Not scheduled'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {item.status === 'failed' && (
                        <button
                          onClick={() => retryMutation.mutate(item.id)}
                          disabled={retryMutation.isPending}
                          className="text-primary-600 hover:text-primary-900 mr-4 disabled:opacity-50"
                        >
                          Retry
                        </button>
                      )}
                      {(item.status === 'pending' || item.status === 'scheduled') && (
                        <button
                          onClick={() => {
                            if (window.confirm('Cancel this scheduled publication?')) {
                              cancelMutation.mutate(item.id);
                            }
                          }}
                          disabled={cancelMutation.isPending}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      )}
                      {item.status === 'published' && (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-lg border bg-white p-4">
          <p className="text-sm text-gray-500">Pending</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {queue.filter((i) => i.status === 'pending').length}
          </p>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <p className="text-sm text-gray-500">Scheduled</p>
          <p className="mt-1 text-2xl font-bold text-blue-600">
            {queue.filter((i) => i.status === 'scheduled').length}
          </p>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <p className="text-sm text-gray-500">Published</p>
          <p className="mt-1 text-2xl font-bold text-green-600">
            {queue.filter((i) => i.status === 'published').length}
          </p>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <p className="text-sm text-gray-500">Failed</p>
          <p className="mt-1 text-2xl font-bold text-red-600">
            {queue.filter((i) => i.status === 'failed').length}
          </p>
        </div>
      </div>
    </div>
  );
}
